import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '@cbhq/data-layer';
import type { ListWorkspaceUserAdminEntitiesResult } from '@cbhq/identity-api-hooks/account-management/fetch';
import { useGetListWorkspaceUserAdminEntitiesCallback } from '@cbhq/identity-api-hooks/account-management/fetch';

import { oauthRedirect } from '../auth/authHelpers';
import { logError, logWarning } from '../utils/logging';

import { useRegisterInitialLoader } from './InitialLoaderProvider';
import type { LegalEntity, Organization, OrganizationContextType } from './OrganizationContext';
import { OrganizationContext } from './OrganizationContext';

export const SELECTED_ORG_KEY = 'workspace:selectedOrganizationId:v1';
export const ORGANIZATION_ID_QUERY_PARAM = 'organization_id';

function getStoredOrganizationId(): string | undefined {
  try {
    const stored = localStorage.getItem(SELECTED_ORG_KEY);
    if (typeof stored === 'string' && stored.length > 0) return stored;
  } catch (err) {
    logWarning(err instanceof Error ? err : new Error(String(err)), {
      context: 'OrganizationProvider.getStoredOrganizationId',
    });
  }
  return undefined;
}

function storeOrganizationId(id: string | undefined): void {
  try {
    if (id) {
      localStorage.setItem(SELECTED_ORG_KEY, id);
    } else {
      localStorage.removeItem(SELECTED_ORG_KEY);
    }
  } catch (err) {
    logWarning(err instanceof Error ? err : new Error(String(err)), {
      context: 'OrganizationProvider.storeOrganizationId',
    });
  }
}

function getQueryParamOrganizationId(search: string): string | undefined {
  const params = new URLSearchParams(search);
  const value = params.get(ORGANIZATION_ID_QUERY_PARAM);
  return value && value.length > 0 ? value : undefined;
}

function hasRequiredOrgFields(
  org: NonNullable<ListWorkspaceUserAdminEntitiesResult['organizations']>[number],
): org is typeof org & { organizationId: string; organizationName: string } {
  if (!org.organizationId || !org.organizationName) {
    logError(new Error('OrganizationProvider: dropping org with missing id or name'), {
      context: 'OrganizationProvider',
      organizationId: org.organizationId,
    });
    return false;
  }
  return true;
}

function hasRequiredLegalEntityFields(
  le: NonNullable<
    NonNullable<ListWorkspaceUserAdminEntitiesResult['organizations']>[number]['legalEntities']
  >[number],
): le is typeof le & { legalEntityId: string; legalEntityName: string } {
  if (!le.legalEntityId || !le.legalEntityName) {
    logError(new Error('OrganizationProvider: dropping legal entity with missing id or name'), {
      context: 'OrganizationProvider',
      legalEntityId: le.legalEntityId,
    });
    return false;
  }
  return true;
}

function toLegalEntity(le: {
  legalEntityId: string;
  legalEntityName: string;
  roles?: string[];
}): LegalEntity {
  return {
    legalEntityId: le.legalEntityId,
    legalEntityName: le.legalEntityName,
    roles: le.roles ?? [],
  };
}

function toOrganization(
  org: {
    organizationId: string;
    organizationName: string;
    roles?: string[];
  },
  legalEntities: LegalEntity[],
): Organization {
  return {
    organizationId: org.organizationId,
    organizationName: org.organizationName,
    legalEntities,
    roles: org.roles ?? [],
  };
}

type OrganizationProviderProps = {
  children: ReactNode;
};

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [organizationsData, setOrganizationsData] = useState<
    ListWorkspaceUserAdminEntitiesResult | undefined
  >(undefined);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const getOrganizations = useGetListWorkspaceUserAdminEntitiesCallback();
  const loading = organizationsData === undefined && fetchError === null;

  useRegisterInitialLoader(loading, 'organization-provider');

  const handleFetchError = useCallback((err: Error) => {
    logError(err, { context: 'OrganizationProvider' });
    // 403 from this endpoint means the user's session has expired (missing/empty
    // UserUUID in the auth context). Send them through the OAuth flow to re-auth.
    // TODO: get backend to make this a 401 instead of 403
    if (err instanceof ApiError && err.status === 403) {
      oauthRedirect();
      return;
    }
    setFetchError(err);
  }, []);

  const retry = useCallback(() => {
    setFetchError(null);
    // This is a hacky way to retry the fetch, by leveraging the dependency array of the useEffect. Revisit this.
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const fetchOrganizations = async () => {
      const response = await getOrganizations();
      if (isCancelled) return;

      if (response instanceof Error) {
        handleFetchError(response);
        return;
      }

      setFetchError(null);
      setOrganizationsData(response);
    };

    void fetchOrganizations();

    return () => {
      isCancelled = true;
    };
  }, [getOrganizations, handleFetchError, retryCount]);

  const error = fetchError;

  const organizations = useMemo<Organization[] | undefined>(
    () =>
      organizationsData?.organizations
        ?.filter(hasRequiredOrgFields)
        .map((org) =>
          toOrganization(
            org,
            (org.legalEntities ?? []).filter(hasRequiredLegalEntityFields).map(toLegalEntity),
          ),
        ),
    [organizationsData],
  );

  const requestedOrganizationId = getQueryParamOrganizationId(location.search);
  const storedOrganizationId = getStoredOrganizationId();

  const orgIds = useMemo(
    () => new Set(organizations?.map((o) => o.organizationId)),
    [organizations],
  );

  const hasRequestedOrganizationAccessDenied =
    requestedOrganizationId != null && orgIds.size > 0 && !orgIds.has(requestedOrganizationId);

  // URL org wins, then saved org, then first org.
  const resolvedId =
    (requestedOrganizationId != null && orgIds.has(requestedOrganizationId)
      ? requestedOrganizationId
      : undefined) ??
    (storedOrganizationId != null && orgIds.has(storedOrganizationId)
      ? storedOrganizationId
      : undefined) ??
    organizations?.[0]?.organizationId;

  useEffect(() => {
    if (!resolvedId) return;
    storeOrganizationId(resolvedId);
  }, [resolvedId]);

  const selectedOrganization = useMemo(
    () => organizations?.find((o) => o.organizationId === resolvedId),
    [organizations, resolvedId],
  );

  const selectOrganization = useCallback(
    (organizationId: string) => {
      if (!orgIds.has(organizationId)) return;
      const params = new URLSearchParams(location.search);
      params.set(ORGANIZATION_ID_QUERY_PARAM, organizationId);
      navigate(`?${params.toString()}`, { replace: true });
    },
    [orgIds, navigate, location.search],
  );

  const value = useMemo<OrganizationContextType>(
    () => ({
      selectedOrganization,
      selectOrganization,
      organizations,
      error,
      retry,
      hasRequestedOrganizationAccessDenied,
    }),
    [
      selectedOrganization,
      selectOrganization,
      organizations,
      error,
      retry,
      hasRequestedOrganizationAccessDenied,
    ],
  );

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
}
